import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  AlertTriangle, Building2, CalendarPlus, Check, ChevronDown, FileKey, MapPin, Pencil, Plus, RefreshCw, Sparkles, Trash2, User,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Select } from '@/components/Select';
import { EmptyState } from '@/components/EmptyState';
import { LoadErrorState } from '@/components/ListStates';
import { useCompanyCurrency, useLabels } from '@/contexts/WorkspaceContext';
import {
  MatchingLead, PropertyDetail as PropertyDetailRow, PropertyUnit, UNIT_STATUSES, UnitStatus, realEstateService,
} from '@/services/real-estate.service';
import PropertyFormModal from './PropertyFormModal';
import UnitFormModal from './UnitFormModal';
import ScheduleViewingModal from './ScheduleViewingModal';
import LeaseFormModal, { rentable } from './LeaseFormModal';
import {
  FURNISHING_LABEL, PROPERTY_TYPE_LABEL, PURPOSE_LABEL, PickedLead, Pill, UNIT_STATUS_LABEL, UNIT_STATUS_VARIANT, formatDay,
  placeLine, unitPrices, unitSpecs, useRealEstateAccess,
} from './shared';

const CLOSED: UnitStatus[] = ['sold', 'off_market'];
const STATUS_DOT: Record<UnitStatus, string> = {
  available: 'bg-success', reserved: 'bg-warning', rented: 'bg-info', sold: 'bg-text-muted', off_market: 'bg-text-muted',
};

export default function PropertyDetail() {
  const { propertyUid = '' } = useParams<{ propertyUid: string }>();
  const navigate = useNavigate();
  const t = useLabels();
  const companyCurrency = useCompanyCurrency();
  const access = useRealEstateAccess();
  const noun = t('properties', 'singular');

  const [property, setProperty] = useState<PropertyDetailRow | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [editOpen, setEditOpen] = useState(false);
  const [unitModal, setUnitModal] = useState<{ unit: PropertyUnit | null } | null>(null);
  const [scheduleFor, setScheduleFor] = useState<{ unit: PropertyUnit; lead: PickedLead | null } | null>(null);
  const [leaseFor, setLeaseFor] = useState<PropertyUnit | null>(null);
  const [busyUnit, setBusyUnit] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [openMatches, setOpenMatches] = useState<string | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setState('loading');
    try {
      const p = await realEstateService.getProperty(propertyUid, { expectedStatuses: [404] });
      setProperty(p);
      setState('ready');
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (!quiet) setState(status === 404 ? 'missing' : 'error');
    }
  }, [propertyUid]);

  useEffect(() => { void load(); }, [load]);

  const replaceUnit = (next: PropertyUnit) =>
    setProperty((p) => (p ? { ...p, units: p.units.map((u) => (u.unitUid === next.unitUid ? { ...u, ...next } : u)) } : p));

  const changeStatus = async (u: PropertyUnit, status: UnitStatus) => {
    if (status === u.status) return;
    setBusyUnit(u.unitUid);
    try {
      const saved = await realEstateService.updateUnit(u.unitUid, { status });
      replaceUnit(saved);
      toast.success(`${u.label} is now ${UNIT_STATUS_LABEL[status].toLowerCase()}`);
    } catch { /* interceptor toasts the refusal */ } finally {
      setBusyUnit(null);
    }
  };

  const removeUnit = async (u: PropertyUnit) => {
    if (!window.confirm(`Delete ${u.label}? Its viewings stay in the history, but the unit disappears from every list.`)) return;
    setBusyUnit(u.unitUid);
    try {
      await realEstateService.removeUnit(u.unitUid);
      toast.success(`${u.label} deleted`);
      void load(true);
    } catch { /* interceptor toasts — e.g. an active lease */ } finally {
      setBusyUnit(null);
    }
  };

  const removeProperty = async () => {
    if (!property) return;
    const n = property.units.length;
    if (!window.confirm(`Delete ${property.name}${n ? ` and its ${n} ${n === 1 ? 'unit' : 'units'}` : ''}? This can’t be undone.`)) return;
    setDeleting(true);
    try {
      await realEstateService.removeProperty(property.propertyUid);
      toast.success(`${property.name} deleted`);
      navigate('/properties');
    } catch { /* interceptor toasts — e.g. an active lease */ } finally {
      setDeleting(false);
    }
  };

  if (state === 'loading' && !property) {
    return (
      <>
        <Topbar title={noun} crumbs={[{ label: t('operations') }, { label: t('properties'), to: '/properties' }, { label: '…' }]} />
        <div className="w-full space-y-4 px-4 py-6 sm:px-7 sm:py-7">
          <div className="skeleton h-40 rounded-2xl" />
          <div className="skeleton h-24 rounded-2xl" />
          <div className="skeleton h-24 rounded-2xl" />
        </div>
      </>
    );
  }

  if (state === 'missing' || state === 'error' || !property) {
    return (
      <>
        <Topbar title={noun} crumbs={[{ label: t('operations') }, { label: t('properties'), to: '/properties' }, { label: 'Not found' }]} />
        <div className="w-full px-4 py-6 sm:px-7 sm:py-7">
          {state === 'error' ? (
            <LoadErrorState what={`this ${noun.toLowerCase()}`} onRetry={() => void load()} />
          ) : (
            <EmptyState
              icon={<Building2 size={28} />}
              title={`This ${noun.toLowerCase()} isn’t here`}
              description="It may have been deleted, or the link is wrong."
              action={<Button variant="secondary" onClick={() => navigate('/properties')}>Back to {t('properties').toLowerCase()}</Button>}
            />
          )}
        </div>
      </>
    );
  }

  const p = property;
  const counts = UNIT_STATUSES.map((s): [UnitStatus, number] => [s, p.units.filter((u) => u.status === s).length]).filter(([, n]) => n > 0);

  return (
    <>
      <Topbar
        title={p.name}
        crumbs={[{ label: t('operations') }, { label: t('properties'), to: '/properties' }, { label: p.name }]}
        description={[PROPERTY_TYPE_LABEL[p.type], placeLine(p)].filter(Boolean).join(' · ')}
        right={(
          <div className="flex items-center gap-2">
            <Button variant="secondary" iconLeft={<Pencil size={14} />} onClick={() => setEditOpen(true)}>Edit</Button>
            <Button variant="ghost" className="text-danger hover:text-danger" iconLeft={<Trash2 size={14} />} loading={deleting} onClick={() => void removeProperty()}>Delete</Button>
          </div>
        )}
      />

      <div className="w-full space-y-5 px-4 py-6 sm:px-7 sm:py-7">
        <Card>
          <div className="flex flex-col gap-5 lg:flex-row">
            {p.imageUrls.length > 0 && (
              <div className="flex shrink-0 gap-2 overflow-x-auto lg:w-72 lg:flex-wrap">
                {p.imageUrls.slice(0, 4).map((src, i) => (
                  <a key={src} href={src} target="_blank" rel="noreferrer" className={clsx('block shrink-0 overflow-hidden rounded-xl bg-surface-alt', i === 0 ? 'h-36 w-56 lg:w-full' : 'h-16 w-20')}>
                    <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" className="h-full w-full object-cover" />
                  </a>
                ))}
              </div>
            )}
            <div className="min-w-0 flex-1 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Pill className="bg-primary-soft text-primary">{PROPERTY_TYPE_LABEL[p.type]}</Pill>
                {p.yearBuilt && <Pill>Built {p.yearBuilt}</Pill>}
                <Pill>{p.units.length} {p.units.length === 1 ? 'unit' : 'units'}</Pill>
                {counts.map(([s, n]) => (
                  <span key={s} className="inline-flex items-center gap-1 text-2xs font-medium text-text-secondary">
                    <span className={clsx('h-1.5 w-1.5 rounded-full', STATUS_DOT[s])} /> {n} {UNIT_STATUS_LABEL[s].toLowerCase()}
                  </span>
                ))}
              </div>
              {(p.address || placeLine(p)) && (
                <p className="flex items-start gap-1.5 text-[13px] text-text-secondary">
                  <MapPin size={14} className="mt-0.5 shrink-0 text-text-muted" />
                  <span>{[p.address, placeLine(p)].filter(Boolean).join(' · ')}</span>
                  {p.latitude !== null && p.latitude !== undefined && p.longitude !== null && p.longitude !== undefined && (
                    <a href={`https://www.google.com/maps?q=${p.latitude},${p.longitude}`} target="_blank" rel="noreferrer" className="shrink-0 text-xs font-semibold text-primary hover:underline">Map</a>
                  )}
                </p>
              )}
              <p className="flex items-center gap-1.5 text-[13px] text-text-secondary">
                <User size={14} className="shrink-0 text-text-muted" />
                {p.ownerContactUid ? (
                  access.contactLinks ? (
                    <Link to={`/contacts/${p.ownerContactUid}`} className="font-medium text-text-primary hover:text-primary hover:underline">{p.ownerName || 'Owner'}</Link>
                  ) : <span className="font-medium text-text-primary">{p.ownerName || 'Owner'}</span>
                ) : (
                  <button type="button" onClick={() => setEditOpen(true)} className="text-text-muted hover:text-primary hover:underline">No owner set — add one</button>
                )}
              </p>
              {p.amenities.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {p.amenities.map((a) => <span key={a} className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs font-medium text-text-secondary">{a}</span>)}
                </div>
              )}
              {p.description && <p className="whitespace-pre-line text-[13px] leading-relaxed text-text-secondary">{p.description}</p>}
            </div>
          </div>
        </Card>

        <section>
          <div className="mb-3 flex items-end justify-between gap-3">
            <div>
              <h2 className="font-display text-base font-semibold tracking-tight text-text-primary">Units</h2>
              <p className="text-xs text-text-muted">Each unit has its own price, status and matching {t('leads').toLowerCase()}.</p>
            </div>
            <Button size="sm" iconLeft={<Plus size={14} />} onClick={() => setUnitModal({ unit: null })}>Add unit</Button>
          </div>

          {p.units.length === 0 ? (
            <EmptyState
              icon={<Building2 size={28} />}
              title="No units yet"
              description="Add what you rent or sell here — an apartment, a villa, a shop. A villa is one property with one unit."
              action={<Button iconLeft={<Plus size={15} />} onClick={() => setUnitModal({ unit: null })}>Add a unit</Button>}
            />
          ) : (
            <div className="space-y-3">
              {p.units.map((u) => (
                <UnitCard
                  key={u.unitUid}
                  unit={u}
                  propertyOwnerUid={p.ownerContactUid}
                  currency={companyCurrency}
                  busy={busyUnit === u.unitUid}
                  matchesOpen={openMatches === u.unitUid}
                  onToggleMatches={() => setOpenMatches((cur) => (cur === u.unitUid ? null : u.unitUid))}
                  onStatus={(s) => void changeStatus(u, s)}
                  onEdit={() => setUnitModal({ unit: u })}
                  onDelete={() => void removeUnit(u)}
                  onSchedule={access.viewings && !CLOSED.includes(u.status) ? (lead) => setScheduleFor({ unit: u, lead }) : undefined}
                  onLease={access.leases && rentable(u) ? () => setLeaseFor(u) : undefined}
                  leaseHref={access.leases && u.currentLeaseUid ? `/leases?lease=${u.currentLeaseUid}` : undefined}
                  leadLinks={access.leadLinks}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      <PropertyFormModal
        open={editOpen}
        property={p}
        onClose={() => setEditOpen(false)}
        onSaved={(saved) => { setEditOpen(false); setProperty(saved); }}
      />
      <UnitFormModal
        open={!!unitModal}
        unit={unitModal?.unit ?? null}
        propertyUid={p.propertyUid}
        propertyType={p.type}
        propertyName={p.name}
        propertyOwnerUid={p.ownerContactUid}
        onClose={() => setUnitModal(null)}
        onSaved={() => { setUnitModal(null); void load(true); }}
      />
      <ScheduleViewingModal
        open={!!scheduleFor}
        unit={scheduleFor?.unit ?? null}
        lead={scheduleFor?.lead ?? null}
        onClose={() => setScheduleFor(null)}
        onSaved={() => setScheduleFor(null)}
      />
      <LeaseFormModal
        open={!!leaseFor}
        unit={leaseFor}
        onClose={() => setLeaseFor(null)}
        onSaved={() => { setLeaseFor(null); void load(true); }}
      />
    </>
  );
}

function UnitCard({
  unit: u, propertyOwnerUid, currency, busy, matchesOpen, onToggleMatches, onStatus, onEdit, onDelete, onSchedule, onLease, leaseHref, leadLinks,
}: {
  unit: PropertyUnit;
  propertyOwnerUid?: string;
  currency: string;
  busy: boolean;
  matchesOpen: boolean;
  onToggleMatches: () => void;
  onStatus: (s: UnitStatus) => void;
  onEdit: () => void;
  onDelete: () => void;
  onSchedule?: (lead: PickedLead | null) => void;
  onLease?: () => void;
  leaseHref?: string;
  leadLinks: boolean;
}) {
  const t = useLabels();
  const prices = unitPrices(u, currency);
  const leased = !!u.currentLeaseUid;
  const ownOwner = u.ownerContactUid && u.ownerContactUid !== propertyOwnerUid ? u.ownerName : null;
  const facts = [
    u.furnishing ? FURNISHING_LABEL[u.furnishing] : null,
    u.parkingSpaces ? `${u.parkingSpaces} parking` : null,
    u.availableFrom && u.status === 'available' ? `Available from ${formatDay(u.availableFrom)}` : null,
  ].filter(Boolean);

  return (
    <div className="rounded-2xl border border-border bg-surface shadow-soft">
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start">
        <div className="flex min-w-0 flex-1 gap-3">
          {u.imageUrls?.[0] ? (
            <img src={u.imageUrls[0]} alt="" loading="lazy" referrerPolicy="no-referrer" className="h-14 w-14 shrink-0 rounded-xl object-cover" />
          ) : (
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary"><Building2 size={20} strokeWidth={1.6} /></span>
          )}
          <div className="min-w-0 space-y-0.5">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <h3 className="text-[14.5px] font-semibold text-text-primary">{u.label}</h3>
              {u.reference && <span className="text-2xs text-text-muted">{u.reference}</span>}
            </div>
            <p className="text-xs text-text-secondary">{unitSpecs(u, true)}</p>
            <p className="text-xs">
              {prices.map((x, i) => (
                <span key={x.kind} className={clsx('tabular-nums', x.missing ? 'text-text-muted' : 'font-semibold text-text-primary')}>
                  {i > 0 && <span className="mx-1 text-text-muted">·</span>}{x.text}
                </span>
              ))}
              <span className="ml-1.5 text-text-muted">{PURPOSE_LABEL[u.purpose]}</span>
            </p>
            {(facts.length > 0 || ownOwner) && (
              <p className="text-2xs text-text-muted">{[...facts, ownOwner ? `Owner: ${ownOwner}` : null].filter(Boolean).join(' · ')}</p>
            )}
            {leased && (
              <p className="flex items-center gap-1 text-2xs font-medium text-info">
                <FileKey size={11} /> Held by an active lease
                {leaseHref && <Link to={leaseHref} className="ml-1 font-semibold text-primary hover:underline">View lease</Link>}
              </p>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:flex-col sm:items-end">
          <div className="w-40">
            <Select
              value={u.status}
              onChange={(e) => onStatus(e.target.value as UnitStatus)}
              disabled={busy || leased}
              title={leased ? 'Follows the active lease — end it to change the status' : 'Change status'}
              aria-label={`Status of ${u.label}`}
              className={clsx('h-9 text-xs font-semibold', UNIT_STATUS_VARIANT[u.status] === 'success' && 'text-success', u.status === 'reserved' && 'text-warning', u.status === 'rented' && 'text-info')}
            >
              {UNIT_STATUSES.map((s) => (
                <option key={s} value={s} disabled={s === 'rented' && u.status !== 'rented'}>
                  {UNIT_STATUS_LABEL[s]}{s === 'rented' && u.status !== 'rented' ? ' (via a lease)' : ''}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 border-t border-border px-4 py-2.5">
        <Button size="sm" variant={matchesOpen ? 'soft' : 'ghost'} iconLeft={<Sparkles size={13} />} iconRight={<ChevronDown size={13} className={clsx('transition-transform', matchesOpen && 'rotate-180')} />} onClick={onToggleMatches} aria-expanded={matchesOpen}>
          Matching {t('leads').toLowerCase()}
        </Button>
        {onSchedule && <Button size="sm" variant="ghost" iconLeft={<CalendarPlus size={13} />} onClick={() => onSchedule(null)}>Schedule viewing</Button>}
        {onLease && <Button size="sm" variant="ghost" iconLeft={<FileKey size={13} />} onClick={onLease}>New lease</Button>}
        <span className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="ghost" iconLeft={<Pencil size={13} />} onClick={onEdit} disabled={busy}>Edit</Button>
          <Button size="sm" variant="ghost" className="text-danger hover:text-danger" iconLeft={<Trash2 size={13} />} onClick={onDelete} disabled={busy || leased} title={leased ? 'End the lease first' : undefined}>Delete</Button>
        </span>
      </div>

      {matchesOpen && <MatchingLeads unit={u} leadLinks={leadLinks} onSchedule={onSchedule} />}
    </div>
  );
}

function MatchingLeads({ unit, leadLinks, onSchedule }: { unit: PropertyUnit; leadLinks: boolean; onSchedule?: (lead: PickedLead) => void }) {
  const t = useLabels();
  const [items, setItems] = useState<MatchingLead[] | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    setItems(null);
    try {
      setItems((await realEstateService.matchingLeads(unit.unitUid)).items);
    } catch {
      setError(true);
    }
  }, [unit.unitUid]);

  useEffect(() => { void load(); }, [load]);

  const leadsWord = t('leads').toLowerCase();

  return (
    <div className="border-t border-border bg-surface-alt/40 px-4 py-3">
      {error ? (
        <LoadErrorState what={`matching ${leadsWord}`} onRetry={() => void load()} bare className="py-6" />
      ) : items === null ? (
        <div className="space-y-2">{Array.from({ length: 2 }).map((_, i) => <div key={i} className="skeleton h-12 rounded-xl" />)}</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-1 py-4 text-center">
          <p className="text-[13px] font-medium text-text-secondary">No open {leadsWord} match {unit.label} yet</p>
          <p className="max-w-md text-2xs text-text-muted">{t('leads')} match on what they asked for — rent or buy, budget, bedrooms, type and area. The WhatsApp AI and your forms fill those in.</p>
          <button type="button" onClick={() => void load()} className="mt-1 inline-flex items-center gap-1 text-2xs font-semibold text-primary hover:underline"><RefreshCw size={11} /> Check again</button>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((m) => {
            const name = m.name || m.phone || 'Unnamed';
            const tone = m.match.score >= 75 ? 'bg-success-soft text-success' : m.match.score >= 55 ? 'bg-primary-soft text-primary' : 'bg-surface-alt text-text-secondary';
            return (
              <li key={m.leadUid} className="rounded-xl border border-border bg-surface p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      {leadLinks ? (
                        <Link to={`/leads/${m.leadUid}`} className="truncate text-[13px] font-semibold text-text-primary hover:text-primary hover:underline">{name}</Link>
                      ) : <span className="truncate text-[13px] font-semibold text-text-primary">{name}</span>}
                      <span className={clsx('rounded-full px-2 py-0.5 text-2xs font-bold tabular-nums', tone)}>{m.match.score}% match</span>
                      <span className="text-2xs capitalize text-text-muted">{m.status}</span>
                    </div>
                    {m.phone && m.name && <a href={`tel:${m.phone}`} className="text-2xs tabular-nums text-text-muted hover:text-primary">{m.phone}</a>}
                  </div>
                  {onSchedule && (
                    <Button size="sm" variant="secondary" iconLeft={<CalendarPlus size={13} />} onClick={() => onSchedule({ leadUid: m.leadUid, name, phone: m.phone, status: m.status })}>
                      Schedule viewing
                    </Button>
                  )}
                </div>
                {(m.match.reasons.length > 0 || m.match.warnings.length > 0) && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.match.reasons.map((r) => (
                      <span key={r} className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-2xs font-medium text-success"><Check size={10} /> {r}</span>
                    ))}
                    {m.match.warnings.map((w) => (
                      <span key={w} className="inline-flex items-center gap-1 rounded-full bg-warning-soft px-2 py-0.5 text-2xs font-medium text-warning"><AlertTriangle size={10} /> {w}</span>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
